import { useMemo, useRef, useCallback, useState, useLayoutEffect } from 'react';
import { SurveyCakeLogicEngine } from '@surveycake/logic-engine';
import {
  useEngineInstance,
  useSubjectId,
  useSubjectAnswers,
  UseSubjectAnswersSelector
} from '@surveycake/react-logic-engine';
import { useLatestValueRef } from '@surveycake/rhooks';
import { debounce } from '@surveycake/utils';
import { useHandleAnswerFinish } from 'app/modules/subject';

function useRatingBarAnswerConfig(engine: SurveyCakeLogicEngine, id: number) {
  return useMemo(() => {
    const data = engine.getSubjectData(id);
    return {
      count: data.upperbounds!,
      shape: (data.extras && data.extras.shape) || ''
    };
  }, [engine, id]);
}

function useRatingIconStyle(count: number) {
  return useMemo(() => {
    if (count === 1) {
      return { width: '100%' };
    }

    const section = Math.floor(100 / count);
    let gap = 3;

    if (6 <= count && count <= 8) {
      gap = 2;
    } else if (count === 9 || count === 10) {
      gap = 1;
    }

    return {
      width: `${section - gap}%`,
      marginRight: `${gap}%`
    };
  }, [count]);
}

function useRatingBarAnswerLevels({
  count,
  shape
}: ReturnType<typeof useRatingBarAnswerConfig>) {
  return useMemo(() => {
    if (typeof count !== 'number' || !shape) {
      return [];
    }

    return Array.from({ length: count }).map((ignored, index) => index + 1);
  }, [count, shape]);
}

const answersSelector: UseSubjectAnswersSelector<number> = answers =>
  (answers.payload[0] || 0) as number;

function useRatingBarValueRef(engine: SurveyCakeLogicEngine, id: number) {
  const value = useSubjectAnswers(id, answersSelector);
  const valueRef = useLatestValueRef(value);
  const setValue = useCallback((level: number) => {
    const payload = valueRef.current !== level && level ? [level] : [];
    engine.setAnswer(id, { payload });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { valueRef, setValue };
}

function useRatingBarActiveLevel(
  engine: SurveyCakeLogicEngine,
  id: number,
  valueRef: ReturnType<typeof useRatingBarValueRef>['valueRef']
) {
  const [activeLevel, setActiveLevel] = useState(valueRef.current);
  const isMounted = useRef(false);
  const handleAnswerFinish = useHandleAnswerFinish(engine, id);
  const debouncedHandleAnswerFinish = useMemo(
    () => debounce(500)(handleAnswerFinish),
    [handleAnswerFinish]
  );

  useLayoutEffect(() => {
    if (valueRef.current !== activeLevel) {
      /**
       * setTimeout for step animation.
       */
      setTimeout(
        () =>
          setActiveLevel(
            activeLevel + (valueRef.current > activeLevel ? 1 : -1)
          ),
        20
      );
    } else if (isMounted.current) {
      debouncedHandleAnswerFinish(valueRef.current === 0);
    }

    isMounted.current = true;
  });

  return activeLevel;
}

export function useRatingBarAnswer() {
  const engine = useEngineInstance();
  const id = useSubjectId();
  const { count, shape } = useRatingBarAnswerConfig(engine, id);
  const style = useRatingIconStyle(count);
  const { valueRef, setValue } = useRatingBarValueRef(engine, id);
  const levels = useRatingBarAnswerLevels({ count, shape });
  const activeLevel = useRatingBarActiveLevel(engine, id, valueRef);
  return levels.map(level => ({
    level,
    shape,
    setValue,
    filled: activeLevel >= level,
    active: activeLevel >= level,
    last: activeLevel === level,
    style
  }));
}
